/**
 * DispatchHistory — collapsed-by-default delivered-orders history for the
 * desktop Dispatch tab (mirrors the PDA "Completed" view).
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, History, Loader2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DispatchHistory() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/dispatch/queue`, { params: { status: "delivered" }, headers: { "x-no-cache": "1" } });
      const data = (res.data || []).slice().sort((a, b) =>
        (b.most_recent_delivered_at || "").localeCompare(a.most_recent_delivered_at || "")
      );
      setRows(data);
    } catch (e) {
      toast.error("Failed to load dispatch history");
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && rows === null) load();
  };

  const fmtDt = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

  return (
    <div className="border rounded-xl bg-white" data-testid="dispatch-history">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-neutral-50 rounded-xl transition-colors"
        data-testid="dispatch-history-toggle"
      >
        <span className="flex items-center gap-2 font-semibold text-brand-navy">
          <History className="w-4 h-4" />
          Delivery history
          <span className="text-xs font-normal text-muted-foreground">(delivered orders — hidden by default)</span>
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-neutral-400" /> : <ChevronDown className="w-4 h-4 text-neutral-400" />}
      </button>

      {open && (
        <div className="px-4 pb-4">
          <div className="flex justify-end mb-2">
            <button
              onClick={load}
              disabled={loading}
              className="text-xs flex items-center gap-1 text-neutral-500 hover:text-brand-navy"
              data-testid="dispatch-history-refresh"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
          {loading && rows === null ? (
            <div className="py-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : !rows || rows.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">No delivered orders yet</div>
          ) : (
            <div className="overflow-x-auto max-h-[480px] overflow-y-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-600 border-b sticky top-0">
                  <tr>
                    <th className="px-3 py-2">Delivered</th>
                    <th className="px-3 py-2">Hotel</th>
                    <th className="px-3 py-2">Order #</th>
                    <th className="px-3 py-2 text-right">Bags</th>
                    <th className="px-3 py-2 text-right">Pieces</th>
                    <th className="px-3 py-2">Run</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((o) => {
                    const pcs = (o.bundles || []).reduce(
                      (s, b) => s + (b.items || []).reduce((t, i) => t + (i.quantity || 0), 0), 0
                    );
                    return (
                      <tr key={o.order_id} className="border-b last:border-0 hover:bg-neutral-50" data-testid={`dispatch-history-row-${o.order_id}`}>
                        <td className="px-3 py-2 whitespace-nowrap text-neutral-600">{fmtDt(o.most_recent_delivered_at)}</td>
                        <td className="px-3 py-2 font-medium">{o.hotel_name}</td>
                        <td className="px-3 py-2 font-mono text-xs">{o.order_number}</td>
                        <td className="px-3 py-2 text-right font-mono">{(o.bundles || []).length}</td>
                        <td className="px-3 py-2 text-right font-mono">{pcs || "—"}</td>
                        <td className="px-3 py-2 text-xs text-neutral-600">{o.run_name || "—"}{o.run_driver ? ` · ${o.run_driver}` : ""}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
